import {useCallback, useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {createSelector} from 'reselect';
import {makeAlertQueryKey} from 'src/modules/profile/user/tabs/content/alerts/util';
import {getUserGeneralAlert} from 'src/openapi/admin/apis/UserGeneralAlertApi';
import {GeneralAlert} from 'src/openapi/admin/models/GeneralAlert';
import {useAppDispatch} from 'src/store/hooks';

import {
    makeSelectAlertsByKey,
    selectReceivedAt,
} from '../slices/alertsSelectors';
import {UserPageState, defaultAlertCriteria, invalidateQuery} from '../slices/alertsSlice';


import {useAlertCriteria} from './useAlertCriteria';

type Props = {
    scopeKey: string,
    userId: number,
    autoload?: boolean,
    ttlMs?: number,
};

export function useUserAlerts({scopeKey, userId, autoload = true, ttlMs = 120_000}: Props) {
    const dispatch = useDispatch();
    const apiDispatch = useAppDispatch();
    const {criteria, setCriteria, setPage, setSort, reset, setPageSize} = useAlertCriteria(scopeKey);

    useEffect(() => {
        if (!criteria) {
            setCriteria(defaultAlertCriteria);
        }
    }, [criteria, setCriteria]);

    const queryKey = useMemo(
        () => (criteria ? makeAlertQueryKey(userId, criteria) : ''),
        [userId, criteria],
    );

    const selectAlerts = useMemo(
        () =>
            queryKey
                ? makeSelectAlertsByKey(queryKey)
                : createSelector([], () => ({
                        items: [] as GeneralAlert[],
                        total: 0,
                        status: 'idle' as const,
                        error: undefined,
                    })),
        [queryKey],
    );

    const {items, total, status, error} = useSelector(selectAlerts);

    const refetch = useCallback(() => {
        if (!criteria) {
            return;
        }
        apiDispatch(getUserGeneralAlert({userId, alertPageRequest: criteria}));
    }, [apiDispatch, userId, criteria]);

    const invalidate = useCallback(() => {
        if (queryKey) {
            dispatch(invalidateQuery(queryKey));
        }
    }, [dispatch, queryKey]);

    const receivedAt = useSelector((state: UserPageState) => selectReceivedAt(state, queryKey));

    useEffect(() => {
        if (!autoload || !criteria) {
            return;
        }

        if (status === 'idle' || status === 'failed') {
            refetch();
            return;
        }

        if (status === 'succeeded' && receivedAt) {
            const now = Date.now();
            if (now - receivedAt > ttlMs) {
                refetch();
            }
        }
    }, [autoload, criteria, status, queryKey, refetch, receivedAt, ttlMs]);

    return {
        data: items,
        total,
        status,
        error,
        criteria,
        setCriteria,
        setPage,
        setPageSize,
        setSort,
        resetCriteria: reset,
        refetch,
        invalidate,
        isFetching: status === 'loading',
    };
}
